import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const SavingOrInvestingScenario = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedAnswers, setSelectedAnswers] = useState({})

  const handleAnswer = (stepNum, answer) => {
    setSelectedAnswers({ ...selectedAnswers, [stepNum]: answer })
  }

  // Calculate decision quality score
  const getDecisionQuality = () => {
    const correctAnswers = {
      4: 'B',  // When will money be needed
      5: 'C',  // Different goals need different decisions
      6: 'C',  // Saving vs investing comparison
      7: 'B',  // Value can go down
      8: 'C',  // Don't copy others
      9: 'D',  // Suspicious offer warning
      10: 'B'  // Final decision based on goal and risk
    }
    
    let goodDecisions = 0
    Object.keys(correctAnswers).forEach(step => {
      if (selectedAnswers[step] === correctAnswers[step]) {
        goodDecisions++
      }
    })
    
    return goodDecisions
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-teal-100 mb-1">Interaktívny scenár</div>
                <h1 className="text-3xl font-bold">📊 Sporenie alebo investovanie?</h1>
              </div>
              {currentStep === 1 ? (
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na ročníky
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              {[...Array(10)].map((_, idx) => (
                <div
                  key={idx + 1}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    idx + 1 <= currentStep ? 'bg-teal-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Krok {currentStep} z 10
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* INTRO SLIDE 1: What this story is about */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">💰</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  O čo pôjde v tomto príbehu?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-teal-50 border-l-4 border-teal-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🏦</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Peniaze, ktoré nepotrebujeme hneď
                      </p>
                      <p className="text-gray-600 text-sm">
                        Niekedy ľudia majú peniaze, ktoré nemusia minúť okamžite
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">👧</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Tvoja úloha
                      </p>
                      <p className="text-gray-600 text-sm">
                        Pomôžeš Nine rozhodnúť, čo má zmysel urobiť s peniazmi, ktoré má odložené
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-cyan-50 border-l-4 border-cyan-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">💡</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Čo sa naučíš
                      </p>
                      <p className="text-gray-600 text-sm">
                        Zistíš rozdiel medzi sporením a investovaním a ako sa rozhodovať rozumne
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-8 py-4 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Začať príbeh →
                </button>
              </div>
            </div>
          )}

          {/* INTRO SLIDE 2: What is saving and what is investing */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">🤔</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Čo je sporenie a čo je investovanie?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🏦</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Sporenie
                      </p>
                      <p className="text-gray-600 text-sm">
                        Peniaze odložíš na bezpečnejšie miesto. Suma zostáva stabilnejšia, ale rastie len pomaly alebo vôbec
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">📈</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Investovanie
                      </p>
                      <p className="text-gray-600 text-sm">
                        Peniaze môžu rásť viac, ale hodnota sa môže aj znížiť. Je to menej stabilné
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">⚖️</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Nie je to to isté
                      </p>
                      <p className="text-gray-600 text-sm">
                        Sporenie a investovanie sú rôzne veci. Každá má svoje výhody aj riziká
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-4 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Pokračovať →
                </button>
              </div>
            </div>
          )}

          {/* INTRO SLIDE 3: What to watch out for */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Na čo si dávať pozor?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">⏱️</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Kedy budeš peniaze potrebovať?
                      </p>
                      <p className="text-gray-600 text-sm">
                        Peniaze na blízky cieľ a na dlhodobý cieľ sa riešia inak
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">⚡</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Riziko je dôležité
                      </p>
                      <p className="text-gray-600 text-sm">
                        Niektoré možnosti sú stabilnejšie, iné rizikovejšie
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🚨</div>
                    <div>
                      <p className="text-gray-700 font-medium mb-2">
                        Sľuby rýchleho zisku sú podozrivé
                      </p>
                      <p className="text-gray-600 text-sm">
                        Ak niekto sľubuje garantovaný rýchly zisk, je to dôvod na podozrenie
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-8 py-4 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition text-lg shadow-lg hover:shadow-xl"
                >
                  Začať rozhodovať →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: When will money be needed? */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">💰</div>
                  <h2 className="text-2xl font-bold text-gray-900">Nina má našetrené peniaze</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Nina má odložených 300 € z darčekov a dlhodobého sporenia. Teraz premýšľa, čo s nimi.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Nina hovorí: <em>"Mám peniaze, ktoré teraz nepotrebujem. Neviem, čo s nimi. Sporenie? Investovanie? Čo je lepšie?"</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si Nine povedal ako prvé?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(4, 'A')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Najdôležitejšie je, ako najrýchlejšie zarobiť viac</div>
                  <div className="text-sm text-gray-600">Rýchly zisk je cieľ</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'B')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Najprv sa opýtaj: Kedy budeš tieto peniaze potrebovať?</div>
                  <div className="text-sm text-gray-600">Čas je dôležitý</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'C')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Investuj všetko, lebo investovanie je vždy lepšie</div>
                  <div className="text-sm text-gray-600">Investovanie = viac peňazí</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(4, 'D')
                    setCurrentStep(5)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Nechaj peniaze doma, v sporení ani investovaní nie je zmysel</div>
                  <div className="text-sm text-gray-600">Peniaze doma sú najlepšie</div>
                </button>
              </div>

              {selectedAnswers[4] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[4] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[4] === 'B' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[4] === 'A' ? 'Rýchly zisk nie je hlavný cieľ. Najprv treba vedieť, kedy peniaze budeš potrebovať.' :
                      selectedAnswers[4] === 'B' ? 'Presne tak! Prvá otázka nie je "ako zarobiť viac", ale "kedy tieto peniaze potrebujem". To ovplyvní rozhodnutie.' :
                      selectedAnswers[4] === 'C' ? 'Investovanie nie je vždy lepšie. Záleží na tom, kedy peniaze potrebuješ a koľko rizika zvládneš.' :
                      'Peniaze doma strácajú hodnotu kvôli inflácii. Sporenie alebo investovanie majú zmysel, ak sa urobia správne.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[4] === 'A' ? 'Snaha o rýchly zisk často vedie k rizikám. Prvá otázka by mala byť: Kedy peniaze budem potrebovať? Podľa toho sa rozhodne, či je lepšie sporenie alebo investovanie.' :
                      selectedAnswers[4] === 'B' ? 'Čas je kľúčový. Ak peniaze budeš potrebovať skoro, je lepšie ich mať na bezpečnejšom mieste. Ak ich nepotrebuješ dlho, môžeš zvážiť investovanie, ktoré má viac rizika, ale aj viac príležitostí.' :
                      selectedAnswers[4] === 'C' ? 'Investovanie môže priniesť viac, ale má aj riziká. Ak peniaze budeš potrebovať skoro, investovanie nemusí byť múdre, lebo hodnota sa môže dočasne znížiť.' :
                      'Peniaze doma strácajú hodnotu kvôli inflácii (ceny tovarov rastú). Sporenie alebo investovanie pomáhajú udržať alebo zvýšiť hodnotu peňazí.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Different goals need different decisions */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🎯</div>
                  <h2 className="text-2xl font-bold text-gray-900">Blízky cieľ vs dlhodobý cieľ</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Nina si uvedomuje: <em>"Časť peňazí možno budem potrebovať o rok na školu v prírode. Ale inú časť nepotrebujem niekoľko rokov."</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Ty sa opýtaš: <em>"Takže máš peniaze na blízky cieľ a peniaze na dlhodobý cieľ. Možno ich treba riešiť inak?"</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Ako by mala Nina rozhodnúť?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(5, 'A')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Všetky peniaze investuj rovnako, nezáleží na cieli</div>
                  <div className="text-sm text-gray-600">Cieľ nie je dôležitý</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'B')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Nechaj všetky peniaze doma, tak ich máš vždy po ruke</div>
                  <div className="text-sm text-gray-600">Doma sú najbezpečnejšie</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'C')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Peniaze na blízky cieľ daj niekam stabilnejšie. Peniaze, ktoré nepotrebuješ dlho, môžeš riešiť inak</div>
                  <div className="text-sm text-gray-600">Každý cieľ inak</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(5, 'D')
                    setCurrentStep(6)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Investuj všetko do najrizikovejšej možnosti, aby si zarobila najviac</div>
                  <div className="text-sm text-gray-600">Riziko = viac peňazí</div>
                </button>
              </div>

              {selectedAnswers[5] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[5] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[5] === 'C' ? '✅ Výborne!' : '⚠️ Nie celkom!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[5] === 'A' ? 'Cieľ je dôležitý! Peniaze, ktoré budeš potrebovať skoro, by mali byť stabilnejšie. Peniaze na dlhší čas môžeš riešiť inak.' :
                      selectedAnswers[5] === 'B' ? 'Peniaze doma strácajú hodnotu kvôli inflácii. Lepšie je použiť sporenie alebo (pre dlhodobé peniaze) investovanie.' :
                      selectedAnswers[5] === 'C' ? 'Presne tak! Peniaze na blízky cieľ by mali byť stabilnejšie (napríklad sporenie). Peniaze, ktoré nepotrebuješ dlho, môžeš riešiť inak (napríklad investovanie).' :
                      'Najrizikovejšia možnosť môže priniesť stratu. Lepšie je prispôsobiť riziko tomu, kedy peniaze potrebuješ.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[5] === 'A' ? 'Rôzne ciele potrebujú rôzne rozhodnutia. Peniaze, ktoré budeš potrebovať skoro, by mali byť na stabilnejšom mieste. Peniaze, ktoré nepotrebuješ niekoľko rokov, môžeš riešiť inak.' :
                      selectedAnswers[5] === 'B' ? 'Peniaze doma strácajú hodnotu kvôli inflácii – ceny tovarov rastú, takže rovnaká suma kúpi menej. Sporenie alebo investovanie pomáhajú chrániť hodnotu peňazí.' :
                      selectedAnswers[5] === 'C' ? 'Peniaze na blízky cieľ (napríklad o rok) by mali byť stabilnejšie, lebo nemáš čas čakať, kým sa hodnota zotaví, ak klesne. Peniaze, ktoré nepotrebuješ dlho, môžeš riešiť rizikovejšie, lebo máš viac času.' :
                      'Najrizikovejšie možnosti môžu priniesť stratu. Ak by Nina investovala všetko do rizikového aktíva a hodnota klesla, nemala by peniaze na školu v prírode. Lepšie je prispôsobiť riziko cieľu.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 6: Saving vs investing comparison */}
          {currentStep === 6 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">⚖️</div>
                  <h2 className="text-2xl font-bold text-gray-900">Porovnanie možností</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Nina porovnáva dve možnosti pre peniaze, ktoré nepotrebuje niekoľko rokov:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2">
                    <li><strong>Sporenie:</strong> Stabilnejšie, ale rastie len pomaly (napríklad 1–2 % ročne)</li>
                    <li><strong>Investovanie:</strong> Môže rásť viac (napríklad 5–7 % ročne), ale hodnota sa môže aj znížiť</li>
                  </ul>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si Nine vysvetlil?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(6, 'A')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Investovanie vždy rastie, sporenie je zbytočné</div>
                  <div className="text-sm text-gray-600">Investovanie = vždy lepšie</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'B')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Sporenie je vždy bezpečnejšie, nikdy neinvestuj</div>
                  <div className="text-sm text-gray-600">Sporenie = jediná možnosť</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'C')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Sporenie je stabilnejšie. Investovanie môže rásť viac, ale hodnota sa môže aj znížiť</div>
                  <div className="text-sm text-gray-600">Každé má svoje výhody aj riziká</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(6, 'D')
                    setCurrentStep(7)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Sporenie aj investovanie sú to isté, nezáleží na tom</div>
                  <div className="text-sm text-gray-600">Je to jedno</div>
                </button>
              </div>

              {selectedAnswers[6] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[6] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[6] === 'C' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[6] === 'A' ? 'Investovanie nerastie vždy. Hodnota sa môže aj znížiť. Sporenie má zmysel pre stabilitu.' :
                      selectedAnswers[6] === 'B' ? 'Sporenie je stabilnejšie, ale pre dlhodobé peniaze môže investovanie priniesť viac. Záleží na cieli a riziku.' :
                      selectedAnswers[6] === 'C' ? 'Presne tak! Sporenie je stabilnejšie, ale rastie pomaly. Investovanie môže rásť viac, ale hodnota sa môže aj znížiť. Každé má svoje miesto.' :
                      'Sporenie a investovanie nie sú to isté. Sporenie je stabilnejšie, investovanie má viac príležitostí aj rizika.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[6] === 'A' ? 'Investovanie môže priniesť viac, ale hodnota sa môže aj znížiť. Nie je to vždy len rast. Sporenie je stabilnejšie a má zmysel pre peniaze, ktoré budeš potrebovať skoro.' :
                      selectedAnswers[6] === 'B' ? 'Sporenie je bezpečnejšie, ale pre dlhodobé peniaze môže investovanie priniesť viac. Ak máš čas (napríklad 5–10 rokov), investovanie môže byť rozumné, aj keď má riziká.' :
                      selectedAnswers[6] === 'C' ? 'Sporenie je stabilnejšie – suma zostáva podobná. Investovanie môže rásť viac, ale hodnota sa môže aj dočasne znížiť. Pre krátky čas je lepšie sporenie, pre dlhý čas môže byť investovanie rozumné.' :
                      'Sporenie a investovanie sú rôzne. Sporenie je stabilnejšie (hodnota sa nemení veľmi), investovanie môže rásť viac, ale má riziká (hodnota sa môže znížiť). Záleží na tom, kedy peniaze potrebuješ.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 7: Value can go down */}
          {currentStep === 7 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">📉</div>
                  <h2 className="text-2xl font-bold text-gray-900">Hodnota klesla</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Nina sa rozhodla investovať časť dlhodobých peňazí. Po niekoľkých mesiacoch zistila, že hodnota klesla z 150 € na 140 €.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Nina hovorí: <em>"Stratila som peniaze! Toto je katastrofa!"</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si Nine vysvetlil?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(7, 'A')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Predaj hneď všetko, investovanie je zlé</div>
                  <div className="text-sm text-gray-600">Musíš okamžite predať</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'B')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Investovanie nerastie vždy. Hodnota sa môže dočasne znížiť, ale časom sa často zotaví</div>
                  <div className="text-sm text-gray-600">Krátkodobé výkyvy sú normálne</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'C')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Investuj ešte viac peňazí, aby si vyrovnala stratu</div>
                  <div className="text-sm text-gray-600">Viac investície = vyrovnanie straty</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(7, 'D')
                    setCurrentStep(8)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Hodnota investície nikdy neklesá, toto je chyba</div>
                  <div className="text-sm text-gray-600">To sa nestáva</div>
                </button>
              </div>

              {selectedAnswers[7] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[7] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[7] === 'B' ? '✅ Výborne!' : '⚠️ Nie celkom!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[7] === 'A' ? 'Predať v panike môže znamenať stratu. Investovanie má výkyvy, ale časom sa hodnota často zotaví. Predať by mala len vtedy, keď to má zmysel.' :
                      selectedAnswers[7] === 'B' ? 'Presne tak! Hodnota investícií sa mení. Krátkodobé poklesy sú normálne. Ak nepotrebuješ peniaze hneď, môžeš počkať, kým sa hodnota zotaví.' :
                      selectedAnswers[7] === 'C' ? 'Investovať viac peňazí do niečoho, čo klesá, môže byť riskantné. Lepšie je pochopiť, prečo hodnota klesla, a rozhodnúť sa rozumne.' :
                      'Hodnota investícií sa mení – môže rásť aj klesať. To je súčasť rizika. Krátkodobé výkyvy sú normálne.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[7] === 'A' ? 'Predať v panike môže znamenať stratu. Investovanie má krátkodobé výkyvy, ale ak máš čas, hodnota sa často časom zotaví. Predať by mala len vtedy, keď to má zmysel (napríklad potrebuješ peniaze).' :
                      selectedAnswers[7] === 'B' ? 'Investovanie nerastie vždy. Hodnota sa môže dočasne znížiť, ale časom sa často zotaví. Ak nepotrebuješ peniaze hneď, môžeš počkať. Preto je investovanie lepšie pre dlhodobé peniaze.' :
                      selectedAnswers[7] === 'C' ? 'Investovať viac peňazí len preto, že hodnota klesla, môže byť riskantné. Lepšie je pochopiť, prečo hodnota klesla, a rozhodnúť sa podľa plánu, nie podľa emócií.' :
                      'Hodnota investícií sa mení – môže rásť aj klesať. Krátkodobé poklesy sú normálne a sú súčasťou rizika. Preto je investovanie lepšie pre dlhodobé peniaze, kde máš čas čakať.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 8: Don't copy others */}
          {currentStep === 8 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">👥</div>
                  <h2 className="text-2xl font-bold text-gray-900">Kamarátka zarobila rýchlo</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Niná kamarátka hovorí: <em>"Investovala som do kryptomien a za týždeň som zarobila 20 %! Musíš to skúsiť!"</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Nina sa cíti tlačená, aby nič nevynechala.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si Nine poradil?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(8, 'A')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Urob to isté ako kamarátka, určite to funguje</div>
                  <div className="text-sm text-gray-600">Keď to fungovalo jej, bude to fungovať aj tebe</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'B')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Investuj rýchlo všetky peniaze, kým nie je neskoro</div>
                  <div className="text-sm text-gray-600">Rýchlosť je kľúčová</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'C')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) To, čo fungovalo niekomu inému, nemusí fungovať tebe. Rozhoduj podľa svojho cieľa a rizika</div>
                  <div className="text-sm text-gray-600">Každý má iný cieľ a situáciu</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(8, 'D')
                    setCurrentStep(9)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Ak kamarátka zarobila, ty zarobíš ešte viac</div>
                  <div className="text-sm text-gray-600">Ty budeš úspešnejšia</div>
                </button>
              </div>

              {selectedAnswers[8] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[8] === 'C' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[8] === 'C' ? '✅ Správne!' : '⚠️ Pozor!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[8] === 'A' ? 'To, čo fungovalo niekomu, nemusí fungovať tebe. Kamarátka mala možno šťastie alebo iný cieľ. Rozhoduj podľa svojej situácie.' :
                      selectedAnswers[8] === 'B' ? 'Investovať rýchlo kvôli strachu, že niečo vynecháš (FOMO – fear of missing out), je riskantné. Rozhoduj podľa plánu, nie emócií.' :
                      selectedAnswers[8] === 'C' ? 'Presne tak! Kamarátka mala možno šťastie, iný cieľ alebo iné riziko. Rozhoduj podľa svojho cieľa, času a rizika, nie podľa toho, čo funguje iným.' :
                      'To, že kamarátka zarobila, neznamená, že ty zarobíš rovnako alebo viac. Každý má inú situáciu a riziko.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[8] === 'A' ? 'To, čo fungovalo niekomu, nemusí fungovať tebe. Kamarátka mala možno šťastie, iný čas vstupu alebo iný cieľ. Rozhoduj podľa svojej situácie, nie podľa toho, čo funguje iným.' :
                      selectedAnswers[8] === 'B' ? 'Investovať rýchlo kvôli strachu, že niečo vynecháš (FOMO – fear of missing out), je časté, ale riskantné. Rozhoduj podľa plánu a cieľa, nie podľa emócií.' :
                      selectedAnswers[8] === 'C' ? 'Kamarátka mala možno šťastie alebo iný cieľ (napríklad krátkodobý zisk). Ty máš svoj cieľ a svoje riziko. Rozhoduj podľa toho, nie podľa toho, čo fungovalo iným.' :
                      'To, že kamarátka zarobila, neznamená, že ty zarobíš rovnako alebo viac. Investovanie má riziká a výsledky sa líšia. Rozhoduj podľa svojho cieľa, nie podľa úspechu iných.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 9: Suspicious offer warning */}
          {currentStep === 9 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🚨</div>
                  <h2 className="text-2xl font-bold text-gray-900">Podozrivá ponuka</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Nina vidí reklamu: <strong>"Investuj 100 € a za mesiac máš garantovaných 200 €! Rýchly zisk bez rizika!"</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Nina hovorí: <em>"To znie super! Bez rizika a rýchly zisk!"</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo by si Nine vysvetlil?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(9, 'A')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) To je skvelá príležitosť, urob to hneď</div>
                  <div className="text-sm text-gray-600">Garantovaný zisk = super</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'B')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Keď je to garantované, určite to funguje</div>
                  <div className="text-sm text-gray-600">Garancia = istota</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'C')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Investuj všetky peniaze, kým to ostatní nevyužijú</div>
                  <div className="text-sm text-gray-600">Musíš byť rýchla</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(9, 'D')
                    setCurrentStep(10)
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) To je podozrivé. Garantovaný rýchly zisk bez rizika neexistuje</div>
                  <div className="text-sm text-gray-600">Príliš dobré na to, aby to bola pravda</div>
                </button>
              </div>

              {selectedAnswers[9] && (
                <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                  selectedAnswers[9] === 'D' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                }`}>
                  <p className="font-semibold text-gray-800 mb-2">
                    {selectedAnswers[9] === 'D' ? '✅ Výborne!' : '⚠️ POZOR!'}
                  </p>
                  <p className="text-gray-700 text-sm mb-3">
                    <strong>Tip:</strong> {
                      selectedAnswers[9] === 'A' ? 'To je podozrivé! Garantovaný rýchly zisk bez rizika neexistuje. Toto môže byť podvod.' :
                      selectedAnswers[9] === 'B' ? 'Garancia rýchleho zisku je varovný signál. Seriózne investície nemajú garančné sľuby o rýchlom zbohatnutí.' :
                      selectedAnswers[9] === 'C' ? 'Investovať všetky peniaze do podozrivej ponuky je veľmi riskantné. Môže to byť podvod.' :
                      'Presne tak! Garantovaný rýchly zisk bez rizika neexistuje. Ak niečo znie príliš dobre, je to dôvod na podozrenie. Toto môže byť podvod.'
                    }
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Vysvetlenie:</strong> {
                      selectedAnswers[9] === 'A' ? 'Garantovaný rýchly zisk bez rizika neexistuje. Ak niečo sľubuje 100 % zisk za mesiac, je to veľmi podozrivé a môže to byť podvod. Seriózne investície nemajú takéto sľuby.' :
                      selectedAnswers[9] === 'B' ? 'Garancia rýchleho zisku je varovný signál. Seriózne investície nemajú garančné sľuby o rýchlom zbohatnutí. Ak niečo znie príliš dobre, je to dôvod na podozrenie.' :
                      selectedAnswers[9] === 'C' ? 'Investovať všetky peniaze do podozrivej ponuky je veľmi riskantné. Ak niečo sľubuje garantovaný rýchly zisk, je to veľmi podozrivé a môže to byť podvod.' :
                      'Garantovaný rýchly zisk bez rizika neexistuje. Ak niečo sľubuje 100 % zisk za mesiac, je to podozrivé a môže to byť podvod. Seriózne investície majú riziká a nemajú takéto sľuby.'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 10: Final decision with quality-based reflection */}
          {currentStep === 10 && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-4xl">🎯</div>
                  <h2 className="text-2xl font-bold text-gray-900">Finálne rozhodnutie</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Nina hovorí: <em>"Takže... Mám 300 €. Časť potrebujem o rok na školu v prírode, časť nepotrebujem niekoľko rokov."</em>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <em>"Sporenie je stabilnejšie, investovanie môže rásť viac, ale hodnota sa môže aj znížiť. A ponuky na rýchly garantovaný zisk sú podozrivé."</em>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-lg font-semibold text-gray-800 mb-4">
                  Čo je najlepšie rozhodnutie pre Ninu?
                </p>

                <button
                  onClick={() => {
                    handleAnswer(10, 'A')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">A) Investuj všetko do najrizikovejšej možnosti, aby zarobila najviac</div>
                  <div className="text-sm text-gray-600">Riziko = viac peňazí</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'B')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">B) Peniaze na blízky cieľ daj do sporenia. Peniaze, ktoré nepotrebuješ dlho, môžeš čiastočne investovať</div>
                  <div className="text-sm text-gray-600">Prispôsob rozhodnutie cieľu a riziku</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'C')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition mb-3"
                >
                  <div className="font-bold text-gray-800 mb-1">C) Nechaj všetky peniaze doma, sporenie ani investovanie nie sú potrebné</div>
                  <div className="text-sm text-gray-600">Doma je najbezpečnejšie</div>
                </button>

                <button
                  onClick={() => {
                    handleAnswer(10, 'D')
                    // Stay on step 10 to show reflection
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-red-400 hover:bg-red-50 transition"
                >
                  <div className="font-bold text-gray-800 mb-1">D) Investuj do podozrivej ponuky, ktorá sľubuje rýchly zisk</div>
                  <div className="text-sm text-gray-600">Rýchly zisk je cieľ</div>
                </button>
              </div>

              {selectedAnswers[10] && (
                <div>
                  <div className={`mt-6 p-6 rounded-lg border-l-4 ${
                    selectedAnswers[10] === 'B' ? 'bg-green-50 border-green-500' : 'bg-orange-50 border-orange-500'
                  }`}>
                    <p className="font-semibold text-gray-800 mb-2">
                      {selectedAnswers[10] === 'B' ? '✅ Výborne!' : '⚠️ Pozor!'}
                    </p>
                    <p className="text-gray-700 text-sm mb-3">
                      <strong>Tip:</strong> {
                        selectedAnswers[10] === 'A' ? 'Najrizikovejšia možnosť môže priniesť stratu. Lepšie je prispôsobiť riziko cieľu a času.' :
                        selectedAnswers[10] === 'B' ? 'Presne tak! Peniaze na blízky cieľ by mali byť stabilnejšie (sporenie). Peniaze, ktoré nepotrebuješ dlho, môžeš čiastočne investovať, ak si ochotná akceptovať riziko.' :
                        selectedAnswers[10] === 'C' ? 'Peniaze doma strácajú hodnotu kvôli inflácii. Sporenie alebo investovanie pomáhajú chrániť hodnotu.' :
                        'Podozrivé ponuky sú veľmi riskantné a môžu byť podvod. Lepšie je použiť seriózne možnosti.'
                      }
                    </p>
                    <p className="text-gray-700 text-sm">
                      <strong>Vysvetlenie:</strong> {
                        selectedAnswers[10] === 'A' ? 'Investovať všetko do najrizikovejšej možnosti môže priniesť stratu. Lepšie je prispôsobiť riziko cieľu: peniaze na blízky cieľ stabilnejšie, peniaze na dlhý čas môžu byť rizikovejšie.' :
                        selectedAnswers[10] === 'B' ? 'Peniaze na blízky cieľ (škola v prírode) by mali byť v sporení, lebo sú stabilnejšie. Peniaze, ktoré nepotrebuješ dlho, môžeš čiastočne investovať – ak hodnota klesne, máš čas čakať na zotavenie.' :
                        selectedAnswers[10] === 'C' ? 'Peniaze doma strácajú hodnotu kvôli inflácii – ceny tovarov rastú. Sporenie alebo investovanie pomáhajú chrániť alebo zvýšiť hodnotu peňazí.' :
                        'Podozrivé ponuky sľubujúce rýchly garantovaný zisk sú veľmi riskantné a môžu byť podvod. Lepšie je použiť seriózne možnosti ako sporenie alebo investovanie.'
                      }
                    </p>
                  </div>

                  {/* Quality-based final reflection */}
                  <div className="mt-8 p-8 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border-2 border-teal-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-2xl">📝</span>
                      Tvoje rozhodovanie
                    </h3>
                    
                    {(() => {
                      const score = getDecisionQuality()
                      
                      if (score >= 6) {
                        return (
                          <div className="space-y-3">
                            <p className="text-green-700 font-semibold">
                              ✅ Výborne! Rozhodoval si veľmi rozumne.
                            </p>
                            <p className="text-gray-700">
                              Pochopil si, že <strong>prvá otázka je čas</strong> – kedy peniaze budeš potrebovať. Uvedomil si, že <strong>sporenie a investovanie sú rôzne veci</strong> – sporenie je stabilnejšie, investovanie môže rásť viac, ale hodnota sa môže aj znížiť.
                            </p>
                            <p className="text-gray-700">
                              Dokázal si rozpoznať, že <strong>hodnota investícií sa mení</strong> – krátkodobé výkyvy sú normálne. A pochopil si, že <strong>nemáš kopírovať rozhodnutia iných</strong> a že <strong>podozrivé ponuky sú varovný signál</strong>.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Záver:</strong> Najlepšie rozhodnutie nie je najrýchlejší možný zisk, ale to, ktoré sa hodí k cieľu, času a riziku.
                            </p>
                          </div>
                        )
                      } else if (score >= 4) {
                        return (
                          <div className="space-y-3">
                            <p className="text-yellow-700 font-semibold">
                              ⚠️ Niektoré rozhodnutia boli dobré, iné menej.
                            </p>
                            <p className="text-gray-700">
                              Niekedy si si uvedomil, že <strong>čas a cieľ sú dôležité</strong> a že <strong>sporenie a investovanie sú rôzne</strong>. To je dobré!
                            </p>
                            <p className="text-gray-700">
                              Ale niekoľkokrát si <strong>podcenil riziká</strong> – investovanie nerastie vždy, hodnota sa môže znížiť. Možno si tiež <strong>ignoroval varovné signály</strong> – podozrivé ponuky alebo kopírovanie rozhodnutí iných.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Čo si zapamätať:</strong> Sporenie je stabilnejšie, investovanie má viac príležitostí aj rizika. Rozhoduj podľa cieľa, času a rizika, nie podľa emócií alebo úspechu iných.
                            </p>
                          </div>
                        )
                      } else {
                        return (
                          <div className="space-y-3">
                            <p className="text-red-700 font-semibold">
                              ⚠️ Tvoje rozhodovanie ignorovalo dôležité varovné signály.
                            </p>
                            <p className="text-gray-700">
                              Niekoľkokrát si <strong>hľadal rýchly zisk</strong> namiesto toho, aby si zvážil čas a cieľ. <strong>Podcenil si riziká</strong> – investovanie nerastie vždy, hodnota sa môže znížiť, a to môže byť problém, ak peniaze budeš potrebovať skoro.
                            </p>
                            <p className="text-gray-700">
                              Možno si tiež <strong>kopíroval rozhodnutia iných</strong> alebo <strong>ignoroval podozrivé ponuky</strong>. To môže viesť k strate peňazí alebo dokonca k podvodu.
                            </p>
                            <p className="text-gray-700 font-medium">
                              💡 <strong>Čo by si mal vedieť:</strong> Sporenie je stabilnejšie – hodnota sa nemení veľmi. Investovanie môže rásť viac, ale hodnota sa môže aj znížiť. Pre peniaze, ktoré budeš potrebovať skoro, je lepšie sporenie. Pre dlhodobé peniaze môže byť investovanie rozumné, ak akceptuješ riziko.
                            </p>
                            <p className="text-gray-700">
                              <strong>Zapamätaj si:</strong> Prvá otázka nie je "ako zarobiť viac", ale "kedy peniaze budem potrebovať". Rozhoduj podľa cieľa, času a rizika. A pozor na podozrivé ponuky – garantovaný rýchly zisk bez rizika neexistuje.
                            </p>
                          </div>
                        )
                      }
                    })()}
                  </div>

                  <div className="mt-8 flex justify-center gap-4">
                    <button
                      onClick={() => navigate('/scenarios/8')}
                      className="px-6 py-3 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition"
                    >
                      Späť na scenáre
                    </button>
                    <button
                      onClick={() => {
                        setCurrentStep(1)
                        setSelectedAnswers({})
                      }}
                      className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition"
                    >
                      Skúsiť znova
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  )
}

export default SavingOrInvestingScenario
